import { useState } from "react";
import {
  FETCH_NOTICE_TYPE,
  DELETE_NOTICE_TYPE,
  ADD_NOTICE_TYPE,
  UPDATE_NOTICE_TYPE,
} from "@root/store/action-types";
import { useTranslation } from "react-i18next";
import { NoticeTypeService } from "@root/services/notice-type";
import { useNotification } from "./useNotification";
import { PAGINATION_DEFAULTS } from "@root/constants";

function useNoticeType() {
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");
  const noticeTypeService = new NoticeTypeService();

  const [noticeTypeLoading, setNoticeTypeloading] = useState(false);
  const [addNoticeTypeLoading, setAddNoticeTypeLoading] = useState(false);
  const [deleteNoticeTypeLoading, setDeleteNoticeTypeLoading] = useState(false);
  const [updateNoticeTypeLoading, setUpdateNoticeTypeLoading] = useState(false);
  const [noticeTypeData, setNoticeTypeData] = useState<any>(null);
  const [pageNo, setPageNo] = useState(1);
  const [search, setSearch] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [newCategory, setNewCategory] = useState("");
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const payload = {
    limit: PAGINATION_DEFAULTS.PAGESIZE,
    offset: (pageNo - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    search: search,
  };

  const onSuccess = (data: any, identifier: any) => {
    if (identifier === FETCH_NOTICE_TYPE) {
      setNoticeTypeloading(false);
      setNoticeTypeData(data?.rows || []);
      setPageCount(data?.count || 0);
    }
    if (identifier === DELETE_NOTICE_TYPE) {
      setDeleteNoticeTypeLoading(false);
      showNotification({
        type: "warning",
        message: t("notice.deleteSuccess"),
      });
      // Refresh the data after adding
      fetchNoticeType(payload);
    }
    if (identifier === ADD_NOTICE_TYPE) {
      setAddNoticeTypeLoading(false);
      // Show success notification for add
      showNotification({
        type: "success",
        message: t("notice.createSuccess"),
      });
      setEditingValue("");
      setAddNewCategory(false);
      setNewCategory("");
      fetchNoticeType(payload);
    }
    if (identifier === UPDATE_NOTICE_TYPE) {
      setUpdateNoticeTypeLoading(false);
      setEditingId(null);
      setEditingValue("");
      showNotification({
        type: "success",
        message: t("notice.updateSuccess"),
      });
      fetchNoticeType(payload);
    }
  };

  const onError = (error: any, identifier: any) => {
    // Fallback message
    const fallbackMessage = t("notice.genericError");

    // Try to extract the actual backend error message
    const errorMessage =
      error?.response?.data?.message || error?.message || fallbackMessage;

    if (identifier === FETCH_NOTICE_TYPE) {
      setNoticeTypeloading(false);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    }

    if (identifier === DELETE_NOTICE_TYPE) {
      setDeleteNoticeTypeLoading(false);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    }

    if (identifier === ADD_NOTICE_TYPE) {
      setAddNoticeTypeLoading(false);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    }

    if (identifier === UPDATE_NOTICE_TYPE) {
      setUpdateNoticeTypeLoading(false);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    }
  };

  const addNoticeType = async (payload: any) => {
    setAddNoticeTypeLoading(true);
    const identifier = ADD_NOTICE_TYPE;
    try {
      const response = await noticeTypeService.addNoticeType(payload);

      // Use passed success callback
      onSuccess(response?.data, identifier);
    } catch (error) {
      // Use passed error callback
      onError(error, identifier);
    }
  };

  const fetchNoticeType = async (payload: any) => {
    setNoticeTypeloading(true);
    const identifier = FETCH_NOTICE_TYPE;
    try {
      const response = await noticeTypeService.fetchNoticeTypes(payload);
      // Use passed success callback
      onSuccess(response?.data.data, identifier);
    } catch (error: unknown) {
      onError(error, identifier);
    }
  };

  const deleteNoticeType = async (id: number) => {
    setDeleteNoticeTypeLoading(true);
    const identifier = DELETE_NOTICE_TYPE;
    try {
      const response = await noticeTypeService.deleteNoticeType(id);
      onSuccess(response?.data, identifier);
    } catch (error: unknown) {
      onError(error, identifier);
    }
  };

  const updateNoticeType = async (id: number, payload: any) => {
    setUpdateNoticeTypeLoading(true);
    const identifier = UPDATE_NOTICE_TYPE;
    try {
      const response = await noticeTypeService.updateNoticeType(id, payload);
      onSuccess(response?.data, identifier);
    } catch (error: unknown) {
      onError(error, identifier);
    }
  };

  return {
    pageCount,
    noticeTypeLoading,
    addNoticeTypeLoading,
    deleteNoticeTypeLoading,
    updateNoticeTypeLoading,
    fetchNoticeType,
    addNoticeType,
    updateNoticeType,
    deleteNoticeType,
    noticeTypeData,
    setNoticeTypeData,
    search,
    setSearch,
    newCategory,
    setNewCategory,
    pageNo,
    setPageNo,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
  };
}
export default useNoticeType;
